/**
 * HTTP Method Security Service
 * Resolves VAPT point: Application allows insecure HTTP methods
 * Implements secure HTTP method restrictions and validation
 */

class HttpMethodSecurityService {
  constructor() {
    // Allowed HTTP methods for the application
    this.allowedMethods = new Set([
      'GET',    // Read operations
      'POST',   // Create operations
      'PUT',    // Update operations (full resource)
      'PATCH',  // Update operations (partial resource)
      'DELETE', // Delete operations
      'HEAD',   // Headers only (for validation)
      'OPTIONS' // CORS preflight (required for browser compatibility)
    ]);

    // Dangerous/insecure HTTP methods that should be blocked
    this.blockedMethods = new Set([
      'TRACE',     // Can expose sensitive headers and enable XST attacks
      'TRACK',     // Similar to TRACE, can be used for attacks
      'CONNECT',   // Can be misused for tunneling
      'PROPFIND',  // WebDAV method, not needed for REST APIs
      'PROPPATCH', // WebDAV method, not needed for REST APIs
      'MKCOL',     // WebDAV method, not needed for REST APIs
      'COPY',      // WebDAV method, not needed for REST APIs
      'MOVE',      // WebDAV method, not needed for REST APIs
      'LOCK',      // WebDAV method, not needed for REST APIs
      'UNLOCK',    // WebDAV method, not needed for REST APIs
      'VERSION-CONTROL', // WebDAV versioning
      'REPORT',    // WebDAV reporting
      'CHECKOUT',  // WebDAV versioning
      'CHECKIN',   // WebDAV versioning
      'UNCHECKOUT', // WebDAV versioning
      'MKWORKSPACE', // WebDAV workspaces
      'UPDATE',    // WebDAV update
      'LABEL',     // WebDAV labeling
      'MERGE',     // WebDAV merging
      'BASELINE-CONTROL', // WebDAV baseline
      'MKACTIVITY' // WebDAV activity
    ]);

    // Method-specific security configurations
    this.methodSecurityConfig = {
      'GET': {
        requiresAuth: true,
        allowedContentTypes: [],
        maxUrlLength: 2048,
        cacheable: true,
        idempotent: true,
        safe: true
      },
      'POST': {
        requiresAuth: true,
        allowedContentTypes: ['application/json', 'multipart/form-data', 'application/x-www-form-urlencoded'],
        maxBodySize: 10 * 1024 * 1024, // 10MB
        cacheable: false,
        idempotent: false,
        safe: false,
        requiresCsrfProtection: true
      },
      'PUT': {
        requiresAuth: true,
        allowedContentTypes: ['application/json', 'multipart/form-data'],
        maxBodySize: 10 * 1024 * 1024, // 10MB
        cacheable: false,
        idempotent: true,
        safe: false,
        requiresCsrfProtection: true
      },
      'PATCH': {
        requiresAuth: true,
        allowedContentTypes: ['application/json', 'application/json-patch+json'],
        maxBodySize: 5 * 1024 * 1024, // 5MB
        cacheable: false,
        idempotent: false,
        safe: false,
        requiresCsrfProtection: true
      },
      'DELETE': {
        requiresAuth: true,
        allowedContentTypes: [],
        cacheable: false,
        idempotent: true,
        safe: false,
        requiresCsrfProtection: true,
        requiresExplicitConfirmation: true
      },
      'HEAD': {
        requiresAuth: true,
        allowedContentTypes: [],
        cacheable: true,
        idempotent: true,
        safe: true
      },
      'OPTIONS': {
        requiresAuth: false, // CORS preflight doesn't include auth
        allowedContentTypes: [],
        cacheable: true,
        idempotent: true,
        safe: true
      }
    };

    this.isInitialized = false;
    this.violations = [];
  }

  /**
   * Initialize HTTP method security
   */
  initialize() {
    if (this.isInitialized) return;

    // Intercept and validate all HTTP requests
    this.setupFetchInterceptor();
    
    // Set up XMLHttpRequest interceptor for legacy code
    this.setupXHRInterceptor();
    
    // Monitor and log method usage
    this.setupMethodMonitoring();
    
    this.isInitialized = true;

  }

  /**
   * Set up fetch API interceptor
   */
  setupFetchInterceptor() {
    const originalFetch = window.fetch;
    
    window.fetch = async (url, options = {}) => {
      try {
        // Validate HTTP method before making request
        const validatedOptions = await this.validateAndSecureRequest(url, options);
        
        // Proceed with validated request
        return await originalFetch(url, validatedOptions);
      } catch (error) {
        console.error('HTTP Method Security blocked request:', error.message);
        throw new Error(`Request blocked by security policy: ${error.message}`);
      }
    };
  }

  /**
   * Set up XMLHttpRequest interceptor
   */
  setupXHRInterceptor() {
    const originalOpen = XMLHttpRequest.prototype.open;
    
    XMLHttpRequest.prototype.open = function(method, url, async = true, user, password) {
      try {
        // Validate method before opening request
        window.httpMethodSecurity.validateMethod(method, url);
        
        // Proceed with original open
        return originalOpen.call(this, method, url, async, user, password);
      } catch (error) {
        console.error('HTTP Method Security blocked XHR request:', error.message);
        throw new Error(`XHR request blocked by security policy: ${error.message}`);
      }
    };
  }

  /**
   * Set up method usage monitoring
   */
  setupMethodMonitoring() {
    // Monitor method usage patterns
    this.methodUsageStats = {
      GET: 0,
      POST: 0,
      PUT: 0,
      PATCH: 0,
      DELETE: 0,
      HEAD: 0,
      OPTIONS: 0,
      blocked: 0
    };

    // Log suspicious patterns
    setInterval(() => {
      this.analyzeUsagePatterns();
    }, 300000); // Check every 5 minutes
  }

  /**
   * Validate and secure HTTP request
   * @param {string} url - Request URL
   * @param {Object} options - Fetch options
   * @returns {Object} - Validated options
   */
  async validateAndSecureRequest(url, options) {
    const method = (options.method || 'GET').toUpperCase();
    
    // Validate HTTP method
    this.validateMethod(method, url);
    
    // Apply method-specific security
    const securedOptions = await this.applyMethodSecurity(method, url, options);
    
    // Track method usage
    this.trackMethodUsage(method);
    
    return securedOptions;
  }

  /**
   * Validate HTTP method
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   */
  validateMethod(method, url) {
    const upperMethod = method.toUpperCase();
    
    // Block dangerous methods
    if (this.blockedMethods.has(upperMethod)) {
      this.logSecurityViolation('BLOCKED_METHOD', upperMethod, url);
      throw new Error(`HTTP method ${upperMethod} is not allowed for security reasons`);
    }
    
    // Only allow whitelisted methods
    if (!this.allowedMethods.has(upperMethod)) {
      this.logSecurityViolation('UNKNOWN_METHOD', upperMethod, url);
      throw new Error(`HTTP method ${upperMethod} is not in the allowed methods list`);
    }
    
    // Method-specific URL validation
    this.validateMethodForUrl(upperMethod, url);
  }

  /**
   * Apply method-specific security controls
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} options - Request options
   * @returns {Object} - Secured options
   */
  async applyMethodSecurity(method, url, options) {
    const securityConfig = this.methodSecurityConfig[method];
    const securedOptions = { ...options };
    
    if (!securityConfig) {
      throw new Error(`No security configuration found for method ${method}`);
    }
    
    // Apply authentication requirements
    if (securityConfig.requiresAuth) {
      securedOptions.headers = await this.ensureAuthentication(securedOptions.headers || {});
    }
    
    // Validate Content-Type for methods that send data
    if (securityConfig.allowedContentTypes && securedOptions.body) {
      this.validateContentType(securedOptions.headers, securityConfig.allowedContentTypes);
    }
    
    // Apply body size limits
    if (securityConfig.maxBodySize && securedOptions.body) {
      this.validateBodySize(securedOptions.body, securityConfig.maxBodySize);
    }
    
    // Apply CSRF protection for state-changing methods
    if (securityConfig.requiresCsrfProtection) {
      securedOptions.headers = this.applyCsrfProtection(securedOptions.headers || {});
    }
    
    // Apply cache control based on method characteristics
    securedOptions.headers = this.applyCacheControl(securedOptions.headers || {}, securityConfig);
    
    // Add security headers
    securedOptions.headers = this.addSecurityHeaders(securedOptions.headers || {}, method);
    
    return securedOptions;
  }

  /**
   * Validate method for specific URL
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   */
  validateMethodForUrl(method, url) {
    // API endpoints should use appropriate methods
    if (url.includes('/api/')) {
      // DELETE should only be used on specific resource endpoints
      if (method === 'DELETE' && !this.isSpecificResourceUrl(url)) {
        throw new Error('DELETE method can only be used on specific resource URLs');
      }
      
      // PUT should only be used on specific resource endpoints
      if (method === 'PUT' && !this.isSpecificResourceUrl(url)) {
        throw new Error('PUT method can only be used on specific resource URLs');
      }
    }
    
    // Authentication endpoints have method restrictions
    if (url.includes('/auth/') || url.includes('/login')) {
      if (!['GET', 'POST', 'OPTIONS'].includes(method)) {
        throw new Error(`Method ${method} not allowed on authentication endpoints`);
      }
    }
  }

  /**
   * Check if URL points to a specific resource
   * @param {string} url - URL to check
   * @returns {boolean} - True if specific resource
   */
  isSpecificResourceUrl(url) {
    // Simple heuristic: specific resource URLs typically end with an ID or have path parameters
    const pathPart = url.split('?')[0]; // Remove query parameters
    const segments = pathPart.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    // Check if last segment looks like an ID (number or UUID pattern)
    return /^\d+$/.test(lastSegment) || // Numeric ID
           /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment); // UUID
  }

  /**
   * Ensure request has proper authentication
   * @param {Object} headers - Request headers
   * @returns {Object} - Headers with authentication
   */
  async ensureAuthentication(headers) {
    // Check if Authorization header is present
    if (!headers.Authorization && !headers.authorization) {
      // Try to get token from auth service
      try {
        const { getAccessToken } = await import('./tokenManager');
        const token = await getAccessToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else {
          throw new Error('No authentication token available for secured method');
        }
      } catch (error) {
        throw new Error('Authentication required for this HTTP method');
      }
    }
    
    return headers;
  }

  /**
   * Validate Content-Type header
   * @param {Object} headers - Request headers
   * @param {Array} allowedTypes - Allowed content types
   */
  validateContentType(headers, allowedTypes) {
    if (allowedTypes.length === 0) return; // No restriction
    
    const contentType = headers['Content-Type'] || headers['content-type'] || '';
    const baseContentType = contentType.split(';')[0].trim();
    
    if (!allowedTypes.includes(baseContentType)) {
      throw new Error(`Content-Type '${baseContentType}' is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }
  }

  /**
   * Validate request body size
   * @param {*} body - Request body
   * @param {number} maxSize - Maximum allowed size
   */
  validateBodySize(body, maxSize) {
    let size = 0;
    
    if (typeof body === 'string') {
      size = new Blob([body]).size;
    } else if (body instanceof FormData) {
      // Approximate size for FormData
      size = body.toString().length;
    } else if (body instanceof ArrayBuffer) {
      size = body.byteLength;
    } else if (body && typeof body === 'object') {
      size = new Blob([JSON.stringify(body)]).size;
    }
    
    if (size > maxSize) {
      throw new Error(`Request body size ${size} exceeds maximum allowed ${maxSize} bytes`);
    }
  }

  /**
   * Apply CSRF protection headers
   * @param {Object} headers - Request headers
   * @returns {Object} - Headers with CSRF protection
   */
  applyCsrfProtection(headers) {
    // Skip CSRF headers to prevent CORS issues
    return headers;
  }

  /**
   * Get CSRF token from various sources
   * @returns {string|null} - CSRF token if available
   */
  getCsrfToken() {
    // Try to get CSRF token from meta tag
    const metaToken = document.querySelector('meta[name="csrf-token"]');
    if (metaToken) {
      return metaToken.getAttribute('content');
    }
    
    // Try to get from session storage
    const sessionToken = sessionStorage.getItem('csrf-token');
    if (sessionToken) {
      return sessionToken;
    }
    
    // Generate a simple client-side token (not as secure as server-generated)
    let clientToken = sessionStorage.getItem('client-csrf-token');
    if (!clientToken) {
      clientToken = this.generateClientCsrfToken();
      sessionStorage.setItem('client-csrf-token', clientToken);
    }
    
    return clientToken;
  }

  /**
   * Generate client-side CSRF token
   * @returns {string} - Generated token
   */
  generateClientCsrfToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Apply cache control headers based on method
   * @param {Object} headers - Request headers
   * @param {Object} securityConfig - Method security configuration
   * @returns {Object} - Headers with cache control
   */
  applyCacheControl(headers, securityConfig) {
    if (!securityConfig.cacheable) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    }
    
    return headers;
  }

  /**
   * Add security headers to request
   * @param {Object} headers - Request headers
   * @param {string} method - HTTP method
   * @returns {Object} - Headers with security additions
   */
  addSecurityHeaders(headers, method) {
    // Skip adding custom headers to prevent CORS issues
    return headers;
  }

  /**
   * Track method usage statistics
   * @param {string} method - HTTP method used
   */
  trackMethodUsage(method) {
    if (this.methodUsageStats[method] !== undefined) {
      this.methodUsageStats[method]++;
    } else {
      this.methodUsageStats.blocked++;
    }
  }

  /**
   * Analyze usage patterns for suspicious activity
   */
  analyzeUsagePatterns() {
    const total = Object.values(this.methodUsageStats).reduce((sum, count) => sum + count, 0);
    
    if (total === 0) return;
    
    // Check for unusual patterns
    const deleteRatio = this.methodUsageStats.DELETE / total;
    const blockedRatio = this.methodUsageStats.blocked / total;
    
    if (deleteRatio > 0.1) { // More than 10% DELETE requests
      console.warn('HTTP Method Security: High DELETE request ratio detected:', deleteRatio);
    }
    
    if (blockedRatio > 0.05) { // More than 5% blocked requests
      console.warn('HTTP Method Security: High blocked request ratio detected:', blockedRatio);
    }
    
    // Reset stats periodically
    if (total > 1000) {
      this.resetStats();
    }
  }

  /**
   * Reset usage statistics
   */
  resetStats() {
    Object.keys(this.methodUsageStats).forEach(key => {
      this.methodUsageStats[key] = 0;
    });
  }

  /**
   * Log security violation
   * @param {string} type - Violation type
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   */
  logSecurityViolation(type, method, url) {
    const violation = {
      type,
      method,
      url,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer
    };
    
    this.violations.push(violation);
    
    // Keep only last 100 violations
    if (this.violations.length > 100) {
      this.violations = this.violations.slice(-100);
    }
    
    console.warn('HTTP Method Security Violation:', violation);
  }

  /**
   * Get security status report
   * @returns {Object} - Security status
   */
  getSecurityStatus() {
    return {
      initialized: this.isInitialized,
      allowedMethods: Array.from(this.allowedMethods),
      blockedMethods: Array.from(this.blockedMethods),
      usageStats: { ...this.methodUsageStats },
      violations: this.violations.length,
      recentViolations: this.violations.slice(-10)
    };
  }

  /**
   * Check if method is secure
   * @param {string} method - HTTP method to check
   * @returns {boolean} - True if method is secure
   */
  isMethodSecure(method) {
    const upperMethod = method.toUpperCase();
    return this.allowedMethods.has(upperMethod) && !this.blockedMethods.has(upperMethod);
  }

  /**
   * Get method security configuration
   * @param {string} method - HTTP method
   * @returns {Object|null} - Security configuration
   */
  getMethodConfig(method) {
    return this.methodSecurityConfig[method.toUpperCase()] || null;
  }
}

// Create and export singleton instance
const httpMethodSecurity = new HttpMethodSecurityService();

// Make it globally available for debugging and XHR interceptor
window.httpMethodSecurity = httpMethodSecurity;

export default httpMethodSecurity;